/**
 * Los límites de la interfaz. UN SOLO SITIO.
 *
 * Cuántas filas se piden, cuántas se enseñan antes de "ver todas", cuántos
 * nombres caben en una alerta. Son decisiones de producto que cambian juntas y
 * que alguien tiene que poder revisar de un vistazo: repartidas por los
 * componentes, cada pantalla acaba con un número distinto puesto a ojo.
 *
 * Los límites del SERVIDOR no están aquí: los pone el backend y los valida él.
 * Estos son los de esta interfaz.
 */

/** Filas que se piden al buscar en el padrón. */
export const PARTICIPANTS_PAGE_SIZE = 100;

/**
 * Lotes que enseña el Resumen antes de "ver todos". Vienen todos --la lista
 * del mes se recorre entera, ver `lib/pages.ts`--; esto es solo cuántos se
 * enseñan de entrada.
 */
export const RECENT_BATCHES_PREVIEW = 5;

/** Mecánicas que enseña la memoria antes de "ver todas". */
export const MECHANICS_PREVIEW = 4;

/**
 * Personas que nombra una alerta antes de rematar con "y N más".
 *
 * `get_period_coverage` pide explícitamente enseñar QUIÉN se quedó fuera y no
 * solo cuántos; tres nombres caben en una línea y ya dicen de quién se habla.
 */
export const ALERT_NAMES_PREVIEW = 3;

/** Meses que ofrece el selector del panel, contando el actual. */
export const PERIOD_CHOICES = 12;

/**
 * La ventana de chat de Work: ancho, alto y el margen que la separa del borde.
 *
 * El alto es el de partida; si la ventana del navegador no da para tanto, se
 * queda con lo que haya menos el margen. El armazón reserva este ancho cuando
 * está abierta --por eso el número vive aquí y no dentro del JSX-- para que la
 * ventana no tape la paginación de una tabla ni el pie de una pantalla.
 */
export const CHAT_BUBBLE_WIDTH = 380;
export const CHAT_BUBBLE_HEIGHT = 520;
export const CHAT_BUBBLE_MARGIN = 16;
/** El botón redondo cuando está cerrada. El armazón le reserva el pie. */
export const CHAT_BUBBLE_BUTTON = 48;

/**
 * Páginas que se piden, como mucho, al traer ENTERA una lista corta (los lotes
 * del mes, las mecánicas). Con el tope del backend, son cientos de filas: si
 * una lista de estas pasa de ahí, ya no es corta y merece otra pantalla.
 */
export const MAX_LIST_PAGES = 10;

/**
 * El diagrama del proceso de revisión: cuánto mide cada caja.
 *
 * El acomodo (elkjs) necesita los tamaños ANTES de dibujar, así que no se
 * miden: se declaran aquí y las cajas los respetan. Cuando los pasos lleven
 * sus cifras vivas, crecer la caja es cambiar este número.
 */
export const REVIEW_FLOW_STEP = { width: 208, height: 76 } as const;
/**
 * El alto de cada línea de las salidas que se dicen dentro de la caja
 * («No se puede determinar → Revisión manual»), y el aire que las separa.
 */
export const REVIEW_FLOW_EXIT_LINE = 15;
export const REVIEW_FLOW_EXIT_GAP = 6;
/** Una rama dentro de unas validaciones en paralelo: más estrecha, van en fila. */
export const REVIEW_FLOW_BRANCH = { width: 176, height: 68 } as const;
export const REVIEW_FLOW_END = { width: 96, height: 40 } as const;
/** Lo que el bloque en paralelo reserva arriba (su nombre) y abajo (cómo se juntan). */
export const REVIEW_FLOW_GROUP_HEADER = 40;
export const REVIEW_FLOW_GROUP_FOOTER = 44;
/**
 * Nombres de resultado que lleva una flecha antes de rematar con "y N más".
 * El detalle del paso los tiene todos.
 */
export const REVIEW_FLOW_LABEL_LINES = 3;

/** Facturas por página en «Facturas en revisión» (el API admite hasta 100). */
export const INVOICE_REVIEWS_PAGE_SIZE = 30;
/**
 * «Reprocesar todas»: cuántas se piden por página para juntarlas (el tope de
 * la lista) y cuántas van en cada llamada (el tope del lote del API).
 */
export const REPROCESS_COLLECT_PAGE_SIZE = 100;
export const REPROCESS_BATCH_MAX = 200;
/**
 * «Revertir» (issue #15, `contrato-revertir.md` §2 y §3): cuántas se piden
 * por página para juntar «todas las del filtro». El tope del API
 * (`INVOICE_REVIEW_REVERT_MAX_IDS`) es 50, tanto por llamada a revertir como
 * por llamada a la vista previa.
 */
export const REVERT_COLLECT_PAGE_SIZE = 100;
export const REVERT_MAX_IDS = 50;
/**
 * El turno de envío a lala4 (`lalaverse-project#68`): cuántas facturas se
 * reponen por llamada a `recover_invoices` (el tope del API, ≤ 200).
 */
export const DISPATCH_TURN_RECOVER_BATCH_MAX = 200;
/**
 * A cuántas horas de `nextOutOfTurnAt` el ícono del turno pasa de ámbar a
 * rojo: ya casi van a salir fuera de turno.
 */
export const DISPATCH_TURN_URGENT_HOURS = 6;
/**
 * Cuántas van en cada llamada AL REVERTIR: una. Cada una recalcula puntos en
 * lala4 (tarda segundos) y el avance tiene que verse factura a factura, «5
 * de 12», no de lote en lote.
 */
export const REVERT_BATCH_MAX = 1;
/** El motivo de revertir (`contrato-revertir.md` §3): el tope del API. */
export const REVERT_REASON_MIN = 3;
export const REVERT_REASON_MAX = 500;
/** El zoom de la foto de una factura: de entrada 1, y estos topes. */
export const INVOICE_IMAGE_MIN_ZOOM = 0.5;
export const INVOICE_IMAGE_MAX_ZOOM = 6;

/**
 * La digitación: los recortes de la foto por sección.
 *
 * El margen alrededor de la sección es una fracción del ANCHO de la foto, y
 * vale lo mismo en vertical: así el aire es igual por los cuatro lados aunque
 * la factura sea una tirilla larga.
 */
export const DATA_ENTRY_CROP_MARGIN = 0.03;
/**
 * Una sección muy alta --los ítems de una tirilla larga-- no se encoge hasta
 * caber entera: ocupa al menos esta fracción del ancho del recorte, y el
 * recorte se centra en el ítem que se está digitando.
 */
export const DATA_ENTRY_TALL_FIT = 0.6;
/**
 * El zoom de partida de un recorte busca que el texto SE LEA, no que quepa
 * la sección entera: deja la altura de una línea de texto --el recuadro de
 * un dato-- en estos píxeles de pantalla, con tope `INVOICE_IMAGE_MAX_ZOOM`.
 * Si así la sección cabe, se ve entera como siempre; si no, se ve su
 * comienzo, o el dato con el foco, y lo demás desplazando.
 */
export const DATA_ENTRY_LEGIBLE_LINE_PX = 19;
/** Cuánto acerca cada pulsación de zoom en un recorte. */
export const DATA_ENTRY_ZOOM_STEP = 1.25;
/** Cuánto desplaza cada flecha: una fracción de lo que se ve. */
export const DATA_ENTRY_PAN_STEP = 0.15;
/** Para saltar a una posición de la cola tecleando su número. */
export const DATA_ENTRY_TYPEAHEAD_MS = 900;
/** Facturas por página en la cola de la digitación. */
export const DATA_ENTRY_PAGE_SIZE = 50;
/**
 * Cada cuánto se refrescan solas la cola y la factura abierta. El API no
 * tiene canal en vivo hacia el navegador: así se ve quién está viendo qué,
 * lo que otros decidieron desaparece y la factura abierta se entera de las
 * líneas que asignó un alias nuevo. Es lo más que tarda en enterarse.
 */
export const DATA_ENTRY_REFRESH_MS = 15_000;
/**
 * Cuánto se espera la imagen de un PDF antes de dejar de decir «Preparando»
 * y ofrecer el archivo original.
 */
export const DATA_ENTRY_IMAGE_WAIT_MS = 45_000;

/**
 * Pasar a la siguiente al instante (`siguiente-rapida.md`, mecanismos 1 y
 * 5): de cuántas de las siguientes probables se precargan los datos y de
 * cuántas la foto. Con más gente trabajando la misma cola se ensancha
 * --sus tomas se llevan justo las primeras predichas-- hasta el tope. Cada
 * foto decodificada son ~48 MB: solo se decodifica la primera.
 */
export const DATA_ENTRY_PREFETCH_ITEMS = 3;
export const DATA_ENTRY_PREFETCH_ITEMS_MAX = 5;
export const DATA_ENTRY_PREFETCH_PHOTOS = 2;
export const DATA_ENTRY_PREFETCH_PHOTOS_MAX = 3;
/** Cuánto espera el navegador ocioso antes de precargar igual. */
export const DATA_ENTRY_PREFETCH_IDLE_MS = 1500;
/**
 * El catálogo de productos se pide entero una vez y se filtra en el
 * navegador (son unas decenas), hasta esta cantidad: si llega a ella, puede
 * haber más, y se vuelve a buscar en el API por lo escrito.
 */
export const DATA_ENTRY_CATALOG_LIMIT = 50;
/**
 * Cuánto se espera a que se deje de teclear para pedir también al API lo
 * escrito (él mira el nombre de lala4, que no llega al navegador): mientras
 * tanto responde el filtro local, al instante.
 */
export const DATA_ENTRY_CATALOG_SEARCH_MS = 250;
/**
 * Buscar por el código de lala4, con la regla del API: el comienzo del
 * código siempre vale; que lo escrito esté en otra parte del código, solo
 * desde estos caracteres («5» no trae todo código con un 5).
 */
export const DATA_ENTRY_CODE_CONTAINS_MIN = 4;
/**
 * Girar la foto (PROVISIONAL, ver `RotatePhotoInput`): cuánto se espera tras
 * la última pulsación antes de mandar el giro al API. Varias seguidas solo
 * guardan la final, sin perder ninguna.
 */
export const DATA_ENTRY_ROTATE_DEBOUNCE_MS = 500;
/**
 * Duplicadas mientras se escribe el número (issue #14): cuánto se espera tras
 * la última pulsación antes de buscar candidatas, y desde cuántos dígitos se
 * busca (menos, y casi cualquier número coincidiría con algo).
 */
export const DATA_ENTRY_DUPLICATE_SEARCH_MS = 400;
export const DATA_ENTRY_DUPLICATE_MIN_DIGITS = 4;

/**
 * La señal de precio de una línea (unitario ÷ referencia), con los MISMOS
 * números que el back (`PRICE_SIGNAL_*` de su `settings.ts`): el front la
 * recalcula al editar la cantidad, el unitario o el subtotal. Si cambian
 * allí, cambian aquí.
 */
export const PRICE_SIGNAL = {
  okMin: 0.6,
  okMax: 1.5,
  multiples: [2, 3, 4, 6, 12, 24, 60],
  multipleTolerance: 0.1,
} as const;

/**
 * Cuánto puede pasarse el subtotal de una línea de cantidad × unitario sin
 * que se avise (redondeos). La del back (`contrato-digitacion.md` §16).
 */
export const LINE_AMOUNTS_TOLERANCE = 0.02;

/**
 * Las tasas de IVA con que un subtotal impreso puede venir YA con el
 * impuesto («Subtotal con IVA 15 %»): las del back
 * (`LINE_AMOUNTS_TAX_RATES`), en su orden: gana la primera que cuadra.
 */
export const LINE_AMOUNTS_TAX_RATES: readonly number[] = [0.15, 0.12];
/** Cuánto hay que quedarse sobre una fila para precargarla. */
export const DATA_ENTRY_PEEK_MS = 150;

/**
 * Cuántas facturas se confirman en lote, como mucho: el tope del back
 * (`REVIEW_CONFIRM_BATCH_MAX`). Sin selección se mandan las cargadas de la
 * pestaña, hasta aquí.
 */
export const DATA_ENTRY_BATCH_MAX = 100;

/**
 * Deshacer en Digitación (`deshacer.md`). Una decisión espera en el
 * navegador `UNDO_MS` desde que se pulsa, y se puede deshacer mientras
 * tanto; con el ratón o el foco en el botón la cuenta se pausa, y al seguir
 * le quedan al menos `RESUME_MIN_MS`, nunca más de `MAX_MS` desde el
 * principio. Las líneas agregadas o quitadas duran lo mismo en la pila.
 */
export const DATA_ENTRY_UNDO_MS = 10_000;
export const DATA_ENTRY_UNDO_RESUME_MIN_MS = 2_000;
export const DATA_ENTRY_UNDO_MAX_MS = 30_000;
/** Cuántas acciones guarda la pila de deshacer. */
export const DATA_ENTRY_UNDO_DEPTH = 5;
/**
 * Antes de retener una decisión, su toma tiene que durar el tope de la
 * espera más este margen; si no, se renueva.
 */
export const DATA_ENTRY_UNDO_CLAIM_MARGIN_MS = 60_000;
/** Cada cuánto se reintenta una decisión que no llegó (la red, un 5xx). */
export const DATA_ENTRY_FLUSH_RETRY_MS = 3_000;
/** Lo retenido que quedó en la pestaña: se manda si tiene menos que esto. */
export const DATA_ENTRY_HELD_MAX_AGE_MS = 15 * 60_000;
/**
 * El tope de un cuerpo con `keepalive` (64 KiB en el navegador, con margen):
 * uno más grande queda solo en la copia de la pestaña.
 */
export const DATA_ENTRY_KEEPALIVE_MAX_BYTES = 60_000;
/** Cuántas retenidas se saltan en «Tomar siguiente» (`REVIEW_CLAIM_EXCLUDE_MAX`). */
export const DATA_ENTRY_CLAIM_EXCLUDE_MAX = 50;

/**
 * «Acierto»: cuántas semanas o meses se ven de una vez, terminando en el del
 * panel, y cada cuánto se repide (el barrido del worker registra cada
 * minuto).
 */
export const ACCURACY_PERIODS_SHOWN = { week: 8, month: 6 } as const;
export const ACCURACY_REFRESH_MS = 60_000;

/**
 * Cada cuánto se repide una factura en revisión mientras sigue en curso: sus
 * pasos avanzan en el worker y el API no avisa al navegador. Terminada o
 * esperando a una persona, ya no cambia sola y no se repide.
 */
export const INVOICE_REVIEW_LIVE_REFRESH_MS = 5_000;

/**
 * El tablero del proceso: cada cuánto se repide lo que hay en cada cola, y
 * desde cuánto esperar el turno se dice en ámbar. Un paso con IA trabaja
 * unos 5 a 30 s: dos minutos de espera ya son una fila.
 */
export const REVIEW_WORKLOAD_REFRESH_MS = 5_000;
export const REVIEW_QUEUE_SLOW_MS = 120_000;

/**
 * Facturas ▸ Resumen: cada cuánto se repide «¿cómo vamos?». Las cifras
 * cambian con cada factura que se decide, pero nadie las mira segundo a
 * segundo; al minuto, como el acierto.
 */
export const INVOICE_PROGRESS_REFRESH_MS = 60_000;

/** Cuántos digitadores se ven en el Resumen antes de «ver todos». */
export const INVOICE_PROGRESS_REVIEWERS_SHOWN = 8;

/**
 * Lo que se espera, como mucho, a cada llamada de la sesión: el refresh del
 * arranque, `/me`, el canje del login y el logout. En producción el gateway
 * sostenía `/auth/refresh` abierto diez minutos con el API sin contestar, y
 * la aplicación se quedaba en blanco todo ese rato. Pasado esto se dice que
 * no se pudo conectar y se ofrece reintentar.
 */
export const AUTH_TIMEOUT_MS = 10_000;

/**
 * Alfred, el buzón: los topes del contrato de `create_alfred_ticket` (los
 * de `create_suggestion`: título 1..120, descripción 1..4000, la ruta hasta
 * 300). Y cuánto se ve el «Listo» en la cabecera de Work, donde no hay
 * barra de estado.
 */
export const ALFRED_TITLE_MAX = 120;
export const ALFRED_DESCRIPTION_MAX = 4_000;
export const ALFRED_PAGE_MAX = 300;
export const ALFRED_THANKS_MS = 5_000;

/**
 * Los topes de Alfred que pone el API, repetidos aquí para decirlos ANTES de
 * subir nada; el API los vuelve a validar. Salen de su `settings.ts`
 * (`ALFRED_ATTACHMENT_*`) y de su agregado (`ALFRED_COMMENT_MAX`,
 * `ALFRED_NOTE_MAX`), y el tamaño y los tipos están también en la
 * descripción de `uploadAlfredAttachment` en `api-types.ts`. Si allí
 * cambian, se cambian aquí.
 */
export const ALFRED_COMMENT_MAX = 4_000;
/** La nota de un cambio de estado y el motivo de anular. */
export const ALFRED_NOTE_MAX = 1_000;
export const ALFRED_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Adjuntos vivos por pedido, contando los de sus comentarios. */
export const ALFRED_ATTACHMENTS_PER_TICKET_MAX = 30;
/** Los tipos que acepta, con la extensión por la que se reconocen. */
export const ALFRED_ATTACHMENT_TYPES: Readonly<Record<string, string>> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
  "image/gif": "gif",
  "application/pdf": "pdf",
  "text/plain": "txt",
  "text/csv": "csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
};
